<template>
    <div class="linii-documente-wrapper">
        <DxDataGrid
            ref="gridRef"
            class="linii-documente-grid"
            height="100%"
            :data-source="dsLinii"
            key-expr="Id"
            show-borders
            :column-auto-width="false"
            :column-min-width="70"
            :allow-column-resizing="true"
            column-resizing-mode="nextColumn"
            :allow-column-reordering="true"
            :focused-row-enabled="true"
            :show-column-lines="true"
            :show-row-lines="true"
            :row-alternation-enabled="true"
            :hover-state-enabled="true"
            :column-chooser="{ enabled: true, mode: 'select', allowSearch: true }"
            :filter-row="{ visible: true }"
            :paging="{ enabled: false }"
            :pager="{ visible: false }"
            @row-inserting="onRowInserting"
            @row-updating="onRowUpdating"
            @init-new-row="onInitNewRow"
            @key-down="onGridKeyDown"
            @initialized="onGridInitialized"
            @cell-prepared="onCellPrepared"
            @editor-preparing="onEditorPreparing"
        >
            <DxStateStoring :enabled="true" type="localStorage" :storage-key="stateStoringKey" />
            <DxSelection mode="single" />
            <DxEditing mode="batch" :allow-updating="isUnlocked" :allow-adding="isUnlocked" :allow-deleting="false"
                :use-icons="true" new-row-position="last" />
            <DxExport :enabled="true" :formats="['pdf', 'xlsx']" />

            <DxToolbar>
                <DxItem location="before" template="deblocheazaBtn" />
                <DxItem v-if="isUnlocked" location="before" template="adaugaLinieBtn" />
                <DxItem v-if="isUnlocked" location="before" template="importExcelBtn" />
                <DxItem v-if="isUnlocked" location="before" template="salveazaTotBtn" />
                <DxItem v-if="isUnlocked && areEtichete" location="before" template="eticheteBtn" />
                <DxItem name="columnChooserButton" />
                <DxItem name="exportButton" />
            </DxToolbar>
            <template #deblocheazaBtn>
                <DxButton :text="isUnlocked ? 'Blochează' : 'Deblochează'" :icon="isUnlocked ? 'unlock' : 'lock'"
                    :type="isUnlocked ? 'normal' : 'default'" styling-mode="outlined" @click="onToggleLock" />
            </template>
            <template #adaugaLinieBtn>
                <DxButton text="Adaugă linie" icon="plus" type="default" styling-mode="outlined"
                    @click="gridRef?.instance?.addRow()" />
            </template>
            <template #importExcelBtn>
                <DxButton text="Import linii din Excel" icon="xlsxfile" type="default" styling-mode="outlined"
                    @click="showImportExcelPopup = true" />
            </template>
            <template #salveazaTotBtn>
                <DxButton text="Salvează tot" icon="save" type="success" styling-mode="contained"
                    :disabled="isSalvandTot" @click="onSalveazaTot" />
            </template>
            <template #eticheteBtn>
                <DxButton text="Etichete" icon="tags" type="default" styling-mode="outlined"
                    @click="onOpenEticheteDocument" />
            </template>

            <!-- Revert linie (rând modificat/nou nesalvat) — la fel ca la Personal-Salarizare -->
            <DxColumn :width="40" caption="" :allow-editing="false" :allow-sorting="false"
                :allow-filtering="false" :fixed="true" fixed-position="left" cell-template="revertCellTemplate" />

            <!-- Produs -->
            <DxColumn data-field="ProdusId" caption="Produs" :min-width="200"
                :calculate-display-value="getProdusNume" edit-cell-template="produsEditor">
                <DxRequiredRule message="Produsul este obligatoriu." />
            </DxColumn>
            <DxColumn data-field="Produs.Cod" caption="Cod" width="110" :allow-editing="false"
                :visible="colVisible('Cod')" />
            <DxColumn data-field="Produs.CodIntern" caption="Cod Intern" width="110" :allow-editing="false"
                :visible="colVisible('CodIntern')" />
            <DxColumn data-field="Nume" caption="Denumire Alternativă" :visible="colVisible('Nume')" />

            <!-- Cantitate / UM / Preț / Valoare -->
            <DxColumn data-field="Cantitate" caption="Cantitate" data-type="number" format="#,##0.00" width="100"
                :visible="colVisible('Cantitate')">
                <DxRequiredRule message="Cantitatea este obligatorie." />
                <DxRangeRule :min="0.0001" message="Cantitatea trebuie să fie mai mare decât 0." />
            </DxColumn>
            <DxColumn data-field="UnitateId" caption="UM" width="90" :visible="colVisible('Unitate')">
                <DxLookup :data-source="dsUnitate ?? []" display-expr="Simbol" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="PretUnitar" caption="Preț Unitar" data-type="number" format="#,##0.0000"
                width="110" :visible="colVisible('PretUnitar')" />
            <DxColumn data-field="Valoare" caption="Valoare" data-type="number" format="#,##0.00" width="110"
                :allow-editing="false" :visible="colVisible('Valoare')" />
            <DxColumn data-field="AdaosUnitar" caption="Adaos/Discount Unitar" data-type="number" format="#,##0.0000"
                width="140" :visible="colVisible('AdaosUnitar')" />
            <DxColumn data-field="Adaos" caption="Adaos/Discount %" data-type="number" format="## '%'" width="110"
                :visible="colVisible('Adaos')" />
            <DxColumn data-field="PretCuAdaos" caption="Preț cu Adaos/Discount" data-type="number" format="#,##0.0000"
                width="140" :allow-editing="false" :visible="colVisible('PretCuAdaos')" />
            <DxColumn data-field="ValoareAdaos" caption="Valoare Adaos/Discount" data-type="number" format="#,##0.00"
                width="140" :visible="colVisible('ValoareAdaos')" />
            <DxColumn data-field="ValoareCuAdaos" caption="Valoare cu Adaos/Discount" data-type="number"
                format="#,##0.00" width="150" :allow-editing="false" :visible="colVisible('ValoareCuAdaos')" />

            <!-- TVA -->
            <DxColumn data-field="Tva" caption="TVA %" data-type="number" format="## '%'" width="90"
                :visible="colVisible('Tva')" />
            <DxColumn data-field="ValoareTva" caption="Valoare TVA" data-type="number" format="#,##0.00" width="110"
                :allow-editing="false" :visible="colVisible('ValoareTva')" />

            <!-- Total -->
            <DxColumn data-field="Total" caption="Total" data-type="number" format="#,##0.00" width="110"
                :allow-editing="false" :visible="colVisible('Total')" />
            <DxColumn caption="Total RON" :calculate-cell-value="calculateTotalRon" data-type="number"
                format="#,##0.00" width="110" :allow-editing="false" :visible="colVisible('TotalRon')" />

            <!-- Contabilitate -->
            <DxColumn data-field="CategorieContabilaId" caption="Categorie Contabilă" width="160"
                :visible="colVisible('CategorieContabila')">
                <DxLookup :data-source="dsCategorieContabila ?? []" display-expr="Nume" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="DebitId" caption="Debit" width="130" :visible="colVisible('Debit')">
                <DxLookup :data-source="dsConturi ?? []" :display-expr="contDisplayExpr" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="CreditId" caption="Credit" width="130" :visible="colVisible('Credit')">
                <DxLookup :data-source="dsConturi ?? []" :display-expr="contDisplayExpr" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="CategTva" caption="Categorie TVA" width="120" :visible="colVisible('CategTva')" />
            <DxColumn data-field="SaftTaxCode" caption="Cod TVA (SAF-T)" width="130"
                :visible="colVisible('SaftTaxCode')" />
            <DxColumn data-field="CategTvaMotivScutire" caption="Categorie TVA Motiv Scutire" width="180"
                :visible="colVisible('CategTvaMotivScutire')" />
            <DxColumn data-field="Custom" caption="Explicație" width="160" :visible="colVisible('Custom')" />

            <!-- Documente gestiune -->
            <DxColumn data-field="CustomId" caption="Scop Operațional" width="160"
                :visible="colVisible('CustomList')">
                <DxLookup :data-source="dsCustomList ?? []" display-expr="Nume" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="GarantieLuni" caption="Garanție (luni)" data-type="number" width="110"
                :visible="colVisible('GarantieLuni')" />
            <DxColumn data-field="LotId" caption="Lot ID" width="110" :allow-editing="false"
                :visible="colVisible('LotId')" />
            <DxColumn data-field="CodNc8" caption="Cod NC8" width="110" :visible="colVisible('CodNc8')" />
            <DxColumn data-field="MasaNet" caption="Masa Net" data-type="number" format="#,##0.0000" width="110"
                :visible="colVisible('MasaNet')" />
            <DxColumn data-field="MasaBrut" caption="Masa Brut" data-type="number" format="#,##0.0000" width="110"
                :visible="colVisible('MasaBrut')" />

            <!-- Personal - Salarizare -->
            <DxColumn data-field="StructIdEditabil" caption="Departament" width="200"
                :calculate-display-value="getDepartamentDisplay" edit-cell-template="departamentEditor"
                :visible="colVisible('Departament')" />

            <!-- Documente gestiune / productie / solduri initiale -->
            <DxColumn data-field="LocDepozitare" caption="Loc Depozitare" width="130"
                :visible="colVisible('LocDepozitare')" />
            <DxColumn data-field="UnitateAmbalajId" caption="UM FA" width="90" :visible="colVisible('UnitateAmbalaj')">
                <DxLookup :data-source="dsUnitate ?? []" display-expr="Simbol" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="CantitateFa" caption="Etichete" data-type="number" format="#,##0.00" width="100"
                :visible="colVisible('CantitateFa')" />
            <DxColumn data-field="CantitatePt1Fa" caption="Cantitate pentru 1 FA" data-type="number" format="#,##0.00"
                width="150" :visible="colVisible('CantitatePt1Fa')" />
            <DxColumn data-field="CodLotFurnizor" caption="Lot Furnizor" width="120"
                :visible="colVisible('CodLotFurnizor')" />
            <DxColumn data-field="CodLotIntern" caption="Lot Intern" width="120"
                :visible="colVisible('CodLotIntern')" />
            <DxColumn data-field="DataExp" caption="Data Expirării" data-type="date" format="dd.MM.yyyy" width="120"
                :visible="colVisible('DataExp')" />
            <DxColumn data-field="TaxaUnitara" caption="Taxă Unitară" data-type="number" format="#,##0.0000"
                width="110" :visible="colVisible('TaxaUnitara')" />

            <!-- Etichete stoc — buton per linie, vizibil doar pe documente de gestiune/MF (areEtichete) -->
            <DxColumn caption="Nr. Etichete" width="100" :allow-editing="false" :allow-sorting="false"
                :allow-filtering="false" :visible="areEtichete" cell-template="eticheteNumarCellTemplate" />

            <!-- Editor produs: DxDropDownBox + grid intern (Cod / Cod Intern / Denumire) -->
            <template #produsEditor="{ data: cellInfo }">
                <DxDropDownBox
                    :value="cellInfo.value"
                    :data-source="dsProdus"
                    :display-expr="produsDisplayExpr"
                    value-expr="Id"
                    :show-clear-button="true"
                    :drop-down-options="{ width: 700, height: 450 }"
                    @opened="onDropBoxOpened"
                    @closed="onDropBoxClosed"
                    @value-changed="(e: any) => { if (e.value == null) cellInfo.setValue(null); }"
                    @key-down="(e: any) => onProdusBoxKeyDown(e, cellInfo)"
                >
                    <template #content>
                        <DxDataGrid
                            ref="innerProdusGridRef"
                            :data-source="dsProdus"
                            :selection="{ mode: 'single' }"
                            :focused-row-enabled="true"
                            :hover-state-enabled="true"
                            :selected-row-keys="cellInfo.value ? [cellInfo.value] : []"
                            :height="410"
                            :show-borders="false"
                            :show-column-lines="false"
                            :show-row-lines="true"
                            :filter-row="{ visible: true }"
                            :paging="{ pageSize: 25 }"
                            key-expr="Id"
                            @content-ready="onProdusGridContentReady"
                            @selection-changed="(e: any) => onProdusGridSelection(e, cellInfo)"
                            @key-down="(e: any) => onProdusGridKeyDown(e, cellInfo)"
                        >
                            <DxColumn data-field="Cod" caption="Cod" :width="110" />
                            <DxColumn data-field="CodIntern" caption="Cod Intern" :width="130" />
                            <DxColumn data-field="Nume" caption="Denumire" />
                        </DxDataGrid>
                    </template>
                </DxDropDownBox>
            </template>

            <!-- Revert linie: vizibil doar dacă rândul are modificări/e nou nesalvat -->
            <template #revertCellTemplate="{ data }">
                <DxButton v-if="isRowDirty(data)" icon="revert" styling-mode="text"
                    hint="Revenire la valoarea inițială" @click="revertRow(data)" />
            </template>

            <!-- Etichete stoc: buton pe linie → emite open-etichete către LiniView (mod linie) -->
            <template #eticheteNumarCellTemplate="{ data }">
                <span style="display:inline-flex;align-items:center;gap:4px;">
                    <DxButton icon="tags" styling-mode="text" hint="Etichete linie"
                        @click="onOpenEticheteLinie(data.data)" />
                    <span v-if="data.data.EticheteNumar">{{ data.data.EticheteNumar }}</span>
                </span>
            </template>

            <!-- Editor departament: DxDropDownBox + DxTreeView (structură arbore ERP_StructuraCompanie) -->
            <template #departamentEditor="{ data: cellInfo }">
                <DxDropDownBox
                    :value="cellInfo.value"
                    :data-source="structuraCompanieItems"
                    value-expr="Id"
                    :display-expr="departamentDisplayExpr"
                    :show-clear-button="true"
                    @value-changed="(e: any) => cellInfo.setValue(e.value ?? null)"
                >
                    <template #content>
                        <DxTreeView
                            :items="structuraCompanieTree"
                            key-expr="Id"
                            display-expr="Nume"
                            selection-mode="single"
                            :select-by-click="true"
                            @item-click="(e: any) => cellInfo.setValue(e.itemData?.Id ?? null)"
                        />
                    </template>
                </DxDropDownBox>
            </template>
        </DxDataGrid>

        <PopupImportLiniiExcel v-model:visible="showImportExcelPopup" :document="props.document"
            @linii-added="reloadAfterSave" />
    </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, onMounted, onUnmounted } from 'vue';
import DxDataGrid, {
    DxColumn,
    DxEditing,
    DxExport,
    DxItem,
    DxLookup,
    DxRangeRule,
    DxRequiredRule,
    DxSelection,
    DxStateStoring,
    DxToolbar,
} from 'devextreme-vue/data-grid';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import DxTreeView from 'devextreme-vue/tree-view';
import DxButton from 'devextreme-vue/button';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
import AppSetting from '@/settings/appSettings';
import http from '@/settings/apiSettings';
import DOC_Linii from '@/services/Documente/DOC_Linii';
import PopupImportLiniiExcel from './PopupImportLiniiExcel.vue';

interface DocumentLiniiProp {
    Id?: string;
    Categorie?: string | null;
    Curs?: number | null;
    TipId?: string | null;
    [key: string]: unknown;
}

const props = defineProps<{ document: DocumentLiniiProp }>();
const emit = defineEmits<{
    (e: 'refresh-document', documentId: string): void;
    (e: 'has-linii-changed', value: boolean): void;
    (e: 'dirty-count-change', value: number): void;
    (e: 'open-etichete', payload: { linieId: string | null }): void;
}>();

// ── Gating Etichete — vizibil doar când tipul documentului e NIVEL_GESTIUNE(0)/NIVEL_MF(2)
// și are cel puțin intrare sau ieșire (regulă portată din legacy DOC_DocumentForm.cs, vezi
// PLAN_IMPLEMENTARE_ETICHETE.md Faza 3). Pattern de fetch identic cu LiniView.vue (onTiparirePDF).
interface TipDocumentInfo {
    CrossCateg?: number | null;
    AreIntrare?: boolean | null;
    AreIesire?: boolean | null;
}
const tipDocumentInfo = ref<TipDocumentInfo | null>(null);

async function fetchTipDocumentInfo() {
    tipDocumentInfo.value = null;
    if (!props.document?.TipId) return;
    try {
        const resp = await http.clientDocumenteOdata.get(
            `DOC_Tipuri(${props.document.TipId})?$select=CrossCateg,AreIntrare,AreIesire`
        );
        tipDocumentInfo.value = resp.data;
    } catch {
        /* ignore — gating rămâne dezactivat dacă tipul nu poate fi citit */
    }
}

const areEtichete = computed(() => {
    const info = tipDocumentInfo.value;
    if (!info) return false;
    return [0, 2].includes(info.CrossCateg ?? -1) && !!(info.AreIntrare || info.AreIesire);
});

watch(() => props.document?.TipId, fetchTipDocumentInfo);

function onOpenEticheteLinie(rowData: any) {
    emit('open-etichete', { linieId: rowData?.Id ?? null });
}

function onOpenEticheteDocument() {
    emit('open-etichete', { linieId: null });
}

// ── Vizibilitate coloane per categorie ──
// Listă de coloane VIZIBILE implicit pentru fiecare categorie de document — restul coloanelor
// rămân declarate o singură dată în grid, doar ascunse implicit (accesibile din Column Chooser).
// Portat 1:1 din vechile componente linii-tabele/*.vue (research verificat pe fiecare fișier sursă).
const CATEGORII_COLOANE_VIZIBILE: Record<string, string[]> = {
    'Contabilitate': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Facturi furnizor': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Tva', 'Total'],
    'Facturi client': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Tva', 'Total'],
    'Mijloace fixe': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Personal - Salarizare': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Documente gestiune': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare'],
    'Solduri Initiale': [
        'Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'PretUnitar', 'Valoare', 'AdaosUnitar', 'Total',
        'LocDepozitare', 'CategorieContabila', 'UnitateAmbalaj', 'CantitateFa', 'CantitatePt1Fa',
        'CodLotFurnizor', 'DataExp', 'CodLotIntern', 'TaxaUnitara',
    ],
    'Documente productie': [
        'Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'PretUnitar', 'Valoare', 'AdaosUnitar', 'Total',
        'LocDepozitare', 'CategorieContabila', 'UnitateAmbalaj', 'CantitateFa', 'CantitatePt1Fa',
        'CodLotFurnizor', 'DataExp', 'CodLotIntern', 'TaxaUnitara',
    ],
};

// Coloane care rămân condiționate de Categorie (LocDepozitare, CodLotFurnizor etc.), mai
// coloanele specifice etichetelor (Etichete/Cant. pt. 1 FA/UM FA) devin vizibile suplimentar
// pe orice document unde `areEtichete===true`, indiferent de textul Categorie — regula reală e
// pe CrossCateg/AreIntrare/AreIesire ale tipului, nu pe eticheta text (vezi PLAN_IMPLEMENTARE_ETICHETE.md).
const COLOANE_ETICHETE = ['CantitateFa', 'CantitatePt1Fa', 'UnitateAmbalaj'];

function colVisible(key: string): boolean {
    const categorie = props.document?.Categorie ?? '';
    const visibleByCategorie = CATEGORII_COLOANE_VIZIBILE[categorie]?.includes(key) ?? false;
    if (visibleByCategorie) return true;
    if (areEtichete.value && COLOANE_ETICHETE.includes(key)) return true;
    return false;
}

const stateStoringKey = computed(() => {
    const slug = (props.document?.Categorie || 'default').toString().replace(/\s+/g, '-').toLowerCase();
    return `grid-document-linii-${slug}`;
});

// ── Grid & date ──
const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
const dsLinii = shallowRef<DataSource | null>(null);
const dsProdus = shallowRef<DataSource | null>(null);
const dsUnitate = shallowRef<DataSource | null>(null);
const dsCategorieContabila = shallowRef<DataSource | null>(null);
const dsConturi = shallowRef<DataSource | null>(null);
const dsCustomList = shallowRef<DataSource | null>(null);
const dsStructuraCompanie = shallowRef<DataSource | null>(null);
const structuraCompanieItems = shallowRef<any[]>([]);
const structuraCompanieTree = shallowRef<any[]>([]);
const isSalvandTot = ref(false);

// ── Blocare/deblocare grid — implicit blocat (read-only), niciun buton de editare
// (Adaugă linie/Import Excel/Salvează tot/Etichete) nu apare până nu se deblochează explicit;
// se reblochează automat la schimbarea documentului afișat, ca să nu rămână "deschis" din greșeală.
const isUnlocked = ref(false);
const showImportExcelPopup = ref(false);

function onToggleLock() {
    isUnlocked.value = !isUnlocked.value;
    if (!isUnlocked.value) gridRef.value?.instance?.cancelEditData();
}

watch(() => props.document?.Id, () => { isUnlocked.value = false; });

function authHeader(e: any) {
    e.headers = {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
    };
}

function toast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', displayTime = 3000) {
    notify(message, type, displayTime);
}

function getErrMsg(err: any): string {
    const data = err?.response?.data;
    if (data) {
        if (typeof data === 'string') return data;
        if (data.error?.message) return data.error.message;
        if (data.message) return data.message;
        if (data.title) return data.title;
    }
    return err?.message ?? 'A apărut o eroare.';
}

function loadDataSources() {
    dsLinii.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'DOC_Linii',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            fieldTypes: { DocumentId: 'Guid', StructIdEditabil: 'Guid' },
            beforeSend: (e: any) => {
                authHeader(e);
                e.params.$expand = [
                    'Produs($select=Cod,CodIntern,Nume,UnitateId,CategorieContabilaId)',
                    'Unitate($select=Simbol)',
                    'CategorieContabila($select=Id,Nume)',
                    'Debit($select=Id,Simbol,Nume)',
                    'Credit($select=Id,Simbol,Nume)',
                    'CustomList($select=Id,Nume)',
                    'StructuraCompanie($select=Id,Descr,Nume)',
                    'UnitateAmbalaj($select=Simbol)',
                ].join(',');
            },
        }),
    });
    dsLinii.value.filter(['DocumentId', '=', props.document.Id]);
    dsLinii.value.load().then((data: any[]) => {
        emit('has-linii-changed', (data?.length ?? 0) > 0);
    });

    dsProdus.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'PRO_Produse',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
        select: ['Id', 'Cod', 'CodIntern', 'Nume', 'UnitateId', 'CategorieContabilaId'],
        paginate: true,
        pageSize: 25,
    });

    dsUnitate.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_ComercialOdata + 'PRO_Unitati',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsCategorieContabila.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'CON_CategoriiContabile',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsConturi.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'CON_Conturi',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsCustomList.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'DOC_CustomList',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsStructuraCompanie.value = new DataSource({
        paginate: false,
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'ERP_StructuraCompanie',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });
    dsStructuraCompanie.value.load().then((items: any[]) => {
        structuraCompanieItems.value = items;
        structuraCompanieTree.value = buildDepartamentTree(items);
    });
}

function buildDepartamentTree(flat: any[], parentId: string | null = null): any[] {
    const pid = parentId != null ? String(parentId).toLowerCase() : null;
    return flat
        .filter((i) => (i.ParentId != null ? String(i.ParentId).toLowerCase() : null) === pid)
        .map((i) => ({ ...i, expanded: true, items: buildDepartamentTree(flat, i.Id) }));
}

function departamentDisplayExpr(item: any): string {
    if (!item) return '';
    return item.Descr ? `${item.Descr} - ${item.Nume || ''}` : item.Nume || '';
}

function getDepartamentDisplay(rowData: any): string {
    return departamentDisplayExpr(rowData?.StructuraCompanie);
}

function contDisplayExpr(item: any): string {
    if (!item) return '';
    return `${item.Simbol || ''} — ${item.Nume || ''}`;
}

function produsDisplayExpr(item: any): string {
    if (!item) return '';
    return `${item.Cod || ''} - ${item.Nume || ''} - ${item.CodIntern || ''}`;
}

function getProdusNume(rowData: any): string {
    return rowData?.Produs?.Nume ?? '';
}

function calculateTotalRon(rowData: any): number | null {
    if (rowData.Total == null || !props.document?.Curs) return null;
    return rowData.Total * (props.document.Curs as number);
}

// ── Dropdown produs: navigare tastatură + auto-completare UM/Categorie Contabilă ──
let currentDropBoxInstance: any = null;
let shouldFocusFilter = false;
const innerProdusGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);

function onDropBoxOpened(e: any) {
    currentDropBoxInstance = e.component;
    shouldFocusFilter = true;
}

function onDropBoxClosed() {
    // currentDropBoxInstance rămâne — mai e nevoie de el pentru option('value')/close() ulterior
}

function onProdusBoxKeyDown(e: any, _cellInfo: any) {
    if (e.event.key === 'Enter') {
        e.event.stopPropagation();
        e.event.preventDefault();
        e.component.open();
    }
}

function onProdusGridContentReady() {
    if (!shouldFocusFilter) return;
    shouldFocusFilter = false;
    window.setTimeout(() => {
        const el = (innerProdusGridRef.value as any)?.$el as HTMLElement | undefined;
        const input = el?.querySelector<HTMLInputElement>('.dx-datagrid-filter-row .dx-texteditor-input');
        input?.focus();
    }, 0);
}

function onProdusGridKeyDown(e: any, cellInfo: any) {
    if (e.event.key !== 'Enter') return;
    e.event.stopPropagation();
    if (e.event.target?.closest?.('.dx-datagrid-filter-row')) return;
    e.event.preventDefault();
    const grid = e.component;
    const focusedRowIndex = grid.option('focusedRowIndex');
    if (focusedRowIndex < 0) return;
    const rowData = grid.getVisibleRows()[focusedRowIndex]?.data;
    if (rowData) onProdusGridSelection({ selectedRowsData: [rowData] }, cellInfo);
}

function onProdusGridSelection(e: any, cellInfo: any) {
    const prod = e.selectedRowsData[0];
    if (!prod) return;
    cellInfo.data.Produs = { Cod: prod.Cod, CodIntern: prod.CodIntern, Nume: prod.Nume };
    if (prod.UnitateId != null) cellInfo.data.UnitateId = prod.UnitateId;
    if (prod.CategorieContabilaId != null) cellInfo.data.CategorieContabilaId = prod.CategorieContabilaId;
    cellInfo.setValue(prod.Id);
    currentDropBoxInstance?.option('value', prod.Id);
    currentDropBoxInstance?.close();
    window.setTimeout(() => {
        gridRef.value?.instance?.editCell(cellInfo.rowIndex, 'Cantitate');
    }, 100);
}

// ── Salvare (adăugare/editare inline, batch) ──
// Payload generic — toate câmpurile posibile ale DOC_CreareLinii, indiferent de categorie.
// Backend-ul (DOC_LiniiRepository.AddLinie/UpdateLinie) ignoră câmpurile irelevante pentru
// tipul de document curent — nu e nevoie de ramificare pe Categorie ca în vechile componente.
// Decizie: NU trimitem niciodată ValoareLinie/ValoareTvaLinie explicit — backend-ul le
// calculează mereu corect din Cantitate/PretUnitar/AdaosLinie, sursa de adevăr rămânând
// întotdeauna backend-ul (confirmat după reload), conform ghidului acestei sarcini.
function buildLiniePayload(row: any) {
    return {
        ProdusId: row.ProdusId ?? null,
        UnitateId: row.UnitateId ?? null,
        // Fallback pe 1, nu 0 — o linie cu Cantitate=0 are mereu Valoare=0, indiferent de preț
        // (vezi onInitNewRow mai sus, care ar trebui să prevină deja acest caz).
        Cantitate: row.Cantitate || 1,
        PretUnitar: row.PretUnitar ?? null,
        TvaLinie: row.Tva ?? null,
        CategorieContabilaId: row.CategorieContabilaId ?? null,
        DebitId: row.DebitId ?? null,
        CreditId: row.CreditId ?? null,
        AdaosLinie: row.Adaos ?? 0,
        AdaosUnitarLinie: row.AdaosUnitar ?? 0,
        ValoareAdaosLinie: row.ValoareAdaos ?? 0,
        ProdusNume: row.Nume ?? null,
        CategTva: row.CategTva ?? null,
        SaftTaxCode: row.SaftTaxCode ?? null,
        CategTvaMotivScutire: row.CategTvaMotivScutire ?? null,
        Custom: row.Custom ?? null,
        CustomId: row.CustomId ?? null,
        GarantieLuni: row.GarantieLuni ?? null,
        CodNc8: row.CodNc8 ?? null,
        MasaNet: row.MasaNet ?? null,
        MasaBrut: row.MasaBrut ?? null,
        StructIdEditabil: row.StructIdEditabil ?? null,
        UnitateAmbalajId: row.UnitateAmbalajId ?? null,
        CantitateFa: row.CantitateFa ?? null,
        CantitatePt1Fa: row.CantitatePt1Fa ?? null,
        CodLotFurnizor: row.CodLotFurnizor ?? null,
        DataExp: row.DataExp ?? null,
        CodLotIntern: row.CodLotIntern ?? null,
        LocDepozitare: row.LocDepozitare ?? null,
        TaxaUnitara: row.TaxaUnitara ?? null,
    };
}

// Cantitate=1 implicit pe rând nou — fără asta, un rând nesalvat rămâne cu Cantitate
// nesetată (undefined), iar la salvare `buildLiniePayload` cădea silențios pe 0, generând
// o linie fără valoare (bug confirmat 2026-07-29: userul vedea "1" în editor dar rândul
// nu avea de fapt nicio valoare setată, deci se salva Cantitate=0/Valoare=0).
function onInitNewRow(e: any) {
    e.data.Cantitate = 1;
}

async function reloadAfterSave() {
    const data = await dsLinii.value?.reload();
    emit('has-linii-changed', ((data as any[])?.length ?? 0) > 0);
    emit('refresh-document', props.document.Id);
}

async function onRowInserting(e: any) {
    const data = { ...e.data };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);
    if (!data.ProdusId) {
        toast('Selectează produsul înainte de a salva linia.', 'error', 3000);
        return;
    }
    try {
        await DOC_Linii.save(props.document.Id, buildLiniePayload(data));
        await reloadAfterSave();
        toast('Linia a fost adăugată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onRowUpdating(e: any) {
    const merged = { ...e.oldData, ...e.newData };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);
    try {
        await DOC_Linii.update(e.key, buildLiniePayload(merged));
        await reloadAfterSave();
        toast('Linia a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

// ── Evidențiere vizuală linie modificată/nouă nesalvată ──
// cellPrepared (nu rowPrepared) — în batch mode DevExtreme repictează celula editată la
// fiecare modificare, dar nu re-declanșează sigur rowPrepared pentru tot rândul; cellPrepared
// e apelat pentru fiecare celulă a rândului afectat, deci colorează tot rândul corect.
function onCellPrepared(e: any) {
    if (e.rowType !== 'data') return;
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const change = changes.find((c) => c.key === e.row?.key);

    // Linie nouă nesalvată — tot rândul e "nou", nu are sens să evidențiem doar un câmp.
    const isNewRow = change?.type === 'insert';
    // Linie existentă editată — evidențiem DOAR celula al cărei câmp a fost chiar modificat,
    // la fel ca `.attr-cell-dirty` din GridAtributeDinamice.vue (Personal-Salarizare).
    const fieldChanged = !isNewRow && !!change && !!e.column?.dataField
        && Object.prototype.hasOwnProperty.call(change.data, e.column.dataField);

    e.cellElement.classList.toggle('linie-noua', isNewRow);
    e.cellElement.classList.toggle('celula-modificata', fieldChanged);

    recomputeDirtyCount();
}

// ── Contor total de câmpuri modificate (afișat ca badge pe tab-ul documentului) ──
// O linie nouă nesalvată numără ca 1 unitate; o linie existentă editată numără câte
// câmpuri diferite are schimbate (Object.keys(change.data).length).
function recomputeDirtyCount() {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; data: any }>;
    let count = 0;
    for (const c of changes) {
        if (c.type === 'insert') count += 1;
        else if (c.type === 'update') count += Object.keys(c.data ?? {}).length;
    }
    emit('dirty-count-change', count);
}

// ── Revert per linie (rând modificat sau nou nesalvat), ca la Personal-Salarizare ──
function isRowDirty(cellInfo: any): boolean {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ key: any }>;
    return changes.some((c) => c.key === cellInfo.key);
}

function revertRow(cellInfo: any) {
    const instance = gridRef.value?.instance;
    if (!instance) return;

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const idx = changes.findIndex((c) => c.key === cellInfo.key);
    if (idx === -1) return;

    if (changes[idx].type === 'insert') {
        // Rând nou, nesalvat încă — se elimină complet din grid.
        instance.deleteRow(cellInfo.rowIndex);
    } else {
        // Editare pe rând existent — se șterge doar modificarea, revine la valorile din server.
        instance.option('editing.changes', changes.filter((_, i) => i !== idx));
    }
    recomputeDirtyCount();
}

// ── Revert per câmp — buton "revert" injectat direct în editor, la fel ca în
// Personal/GeneralTab.vue (markEditor: editor.option('buttons', [{name:'revertInitial', ...}])).
// Apare doar când câmpul respectiv chiar diferă de valoarea originală din server.
const EDITOR_REVERT_BUTTON_NAME = 'revertField';

function currentEditingChange(rowKey: any) {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    return changes.find((c) => c.key === rowKey);
}

function applyFieldRevertButton(editorInstance: any, dataField: string, rowKey: any, originalValue: any) {
    if (!editorInstance?.option) return;
    const change = currentEditingChange(rowKey);
    const isDirty = !!change && change.type === 'update' && Object.prototype.hasOwnProperty.call(change.data, dataField);

    const current = (editorInstance.option('buttons') ?? []) as Array<{ name?: string }>;
    const defaults = current.filter((b) => b?.name !== EDITOR_REVERT_BUTTON_NAME);
    const hasRevert = current.some((b) => b?.name === EDITOR_REVERT_BUTTON_NAME);

    if (isDirty && !hasRevert) {
        editorInstance.option('buttons', [
            {
                name: EDITOR_REVERT_BUTTON_NAME,
                location: 'before',
                options: {
                    icon: 'revert',
                    stylingMode: 'text',
                    hint: 'Revenire la valoarea inițială',
                    onClick: () => revertField(rowKey, dataField, originalValue, editorInstance),
                },
            },
            ...defaults,
        ]);
    } else if (!isDirty && hasRevert) {
        editorInstance.option('buttons', defaults);
    }
}

function revertField(rowKey: any, dataField: string, originalValue: any, editorInstance: any) {
    const instance = gridRef.value?.instance;
    if (!instance) return;

    editorInstance?.option?.('value', originalValue);

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const idx = changes.findIndex((c) => c.key === rowKey);
    if (idx === -1) return;

    const newData = { ...changes[idx].data };
    delete newData[dataField];

    if (Object.keys(newData).length === 0) {
        instance.option('editing.changes', changes.filter((_, i) => i !== idx));
    } else {
        const newChanges = [...changes];
        newChanges[idx] = { ...changes[idx], data: newData };
        instance.option('editing.changes', newChanges);
    }
    recomputeDirtyCount();
}

function onEditorPreparing(e: any) {
    if (e.parentType !== 'dataRow' || !e.dataField) return;

    const dataField = e.dataField;
    const rowKey = e.row?.key;
    const originalValue = e.row?.data?.[dataField];

    const defaultValueChanged = e.editorOptions.onValueChanged;
    e.editorOptions.onValueChanged = function (args: any) {
        defaultValueChanged?.(args);
        applyFieldRevertButton(args.component, dataField, rowKey, originalValue);
    };
}

async function onSalveazaTot() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    if (changes.length === 0) {
        instance.cancelEditData();
        return;
    }

    isSalvandTot.value = true;
    let errors = 0;
    try {
        for (const change of changes) {
            try {
                if (change.type === 'insert') {
                    if (!change.data.ProdusId) throw new Error('Selectează produsul pentru linia nouă.');
                    await DOC_Linii.save(props.document.Id, buildLiniePayload(change.data));
                } else if (change.type === 'update') {
                    const rowData = instance.getVisibleRows().find((r: any) => r.key === change.key)?.data ?? {};
                    await DOC_Linii.update(change.key, buildLiniePayload({ ...rowData, ...change.data }));
                }
            } catch (err: any) {
                errors++;
                toast(getErrMsg(err), 'error', 3000);
            }
        }
        instance.cancelEditData();
        await reloadAfterSave();
        if (errors === 0) toast('Modificările au fost salvate.', 'success', 1500);
    } finally {
        isSalvandTot.value = false;
    }
}

// ── Enter în grid → "Salvează tot", cu excepția dropdown-ului de produs deschis ──
function onGridKeyDown(e: any) {
    if (e.event?.key !== 'Enter') return;
    if (currentDropBoxInstance?.option('opened')) return;
    setTimeout(() => {
        if (currentDropBoxInstance?.option('opened')) return;
        onSalveazaTot();
    }, 0);
}

// ── Auto-save la focusout din grid (pattern DevExtreme batch mode, portat din Comenzi) ──
function onGridFocusOut() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    setTimeout(() => {
        const changes = (instance.option('editing.changes') ?? []) as any[];
        if (changes.length === 0) return;

        const active = window.document.activeElement as HTMLElement | null;
        if (active?.closest('.linii-documente-grid')) return;
        if (active?.closest('.dx-overlay-wrapper')) return;
        onSalveazaTot();
    }, 150);
}

function onGridInitialized(e: any) {
    (e.component.element() as HTMLElement).addEventListener('focusout', onGridFocusOut);
}

onMounted(() => {
    loadDataSources();
    fetchTipDocumentInfo();
});

onUnmounted(() => {
    (gridRef.value?.instance?.element() as HTMLElement | undefined)?.removeEventListener('focusout', onGridFocusOut);
});

defineExpose({ reload: reloadAfterSave });
</script>

<style scoped>
.linii-documente-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.linii-documente-grid {
    flex: 1;
    min-height: 0;
}

:deep(.dx-datagrid) {
    border-radius: 12px;
    overflow: hidden;
}

/* ── Densitate compactă, la fel ca la Comenzi (GridComandaDetalii.vue) ── */
.linii-documente-grid :deep(.dx-row > td) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 11px !important;
    line-height: 1.1 !important;
}

.linii-documente-grid :deep(.dx-header-row > td) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: 24px !important;
    font-size: 11px !important;
    line-height: 1.1 !important;
}

.linii-documente-grid :deep(.dx-toolbar .dx-button) {
    height: 22px !important;
    min-height: unset !important;
}

.linii-documente-grid :deep(.dx-toolbar .dx-button .dx-button-content) {
    padding: 1px 7px !important;
    font-size: 11px !important;
}

.linii-documente-grid :deep(.dx-toolbar .dx-button .dx-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

.linii-documente-grid :deep(.dx-datagrid-header-panel) {
    padding: 3px 6px !important;
}

.linii-documente-grid :deep(.dx-toolbar) {
    height: 32px !important;
}

.linii-documente-grid :deep(.dx-toolbar-items-container) {
    height: 32px !important;
}

.linii-documente-grid :deep(.dx-datagrid-header-panel .dx-texteditor-input) {
    height: 26px !important;
    min-height: unset !important;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

.linii-documente-grid :deep(.dx-datagrid-header-panel .dx-texteditor) {
    height: 26px !important;
}

.linii-documente-grid :deep(.dx-datagrid-filter-row td) {
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

.linii-documente-grid :deep(.dx-datagrid-filter-row .dx-texteditor) {
    height: 26px !important;
}

.linii-documente-grid :deep(.dx-datagrid-filter-row .dx-texteditor-input) {
    height: 26px !important;
    min-height: unset !important;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

.linii-documente-grid :deep(.dx-data-row .dx-button) {
    height: 18px !important;
    width: 18px !important;
    min-height: unset !important;
    min-width: unset !important;
}

.linii-documente-grid :deep(.dx-data-row .dx-button .dx-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

.linii-documente-grid :deep(.dx-data-row .dx-button .dx-icon-revert) {
    color: #ef4444 !important;
}

/* ── Evidențiere linie nouă / celulă modificată — paletă identică cu Personal-Salarizare
   (.attr-cell-dirty din GridAtributeDinamice.vue, .field-dirty din GeneralTab.vue) ── */
.linii-documente-grid :deep(.celula-modificata) {
    background-color: #fff1f2 !important;
    color: #b91c1c !important;
    font-weight: 600;
}

.linii-documente-grid :deep(.linie-noua) {
    background-color: #fff1f2 !important;
}
</style>
